import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ODINBTC_ID } from '../constants/tokenIds'
import { Chain, PlugToken } from '../interfaces/common'
import useConfig from './useConfig'

export const useTokenPicker = ({
  preselectChain,
  onTokenSelect,
  onClose,
  tokensForSelect,
}: {
  preselectChain: string | undefined
  onTokenSelect: (token: PlugToken) => void
  onClose: () => void
  tokensForSelect: PlugToken[]
}) => {
  const [chainMode, setChainMode] = useState<string>('all')
  const [type, setType] = useState<'token' | 'chain'>('token')
  const [visibleChainCount, setVisibleChainCount] = useState(5)

  const { t } = useTranslation()
  const { evmChains, tokens } = useConfig()

  useEffect(() => {
    if (preselectChain) setChainMode(preselectChain)
  }, [preselectChain])

  const getChainInfo = useCallback(
    (chain: string) => {
      const evmChain = evmChains.find((c) => c.chainId.toString() === chain)

      return {
        name: evmChain?.name || t(`chains.long.${chain}`),
        symbol: evmChain?.symbol || t(`chains.short.${chain}`),
      }
    },
    [evmChains]
  )

  const onSelect = useCallback(
    (token: PlugToken) => {
      onTokenSelect(token)
      onClose()
    },
    [onTokenSelect, onClose]
  )

  const onChainSelect = useCallback(
    (chain: string) => {
      setType('token')
      setChainMode(chain)
    },
    [setType, setChainMode]
  )

  const chains = useMemo(() => {
    return Array.from(['all', ...new Set(tokensForSelect.map((t) => t.chain?.toString()))]).filter(
      (c): c is string => !!c
    )
  }, [tokensForSelect])

  const visibleChains = useMemo(() => {
    let visible = chains

    if (chains.length === visibleChainCount + 1) {
      setVisibleChainCount(visibleChainCount + 1)
    } else {
      visible = chains.slice(0, visibleChainCount)

      if (!visible.includes(chainMode)) {
        visible.splice(visibleChainCount - 1, 0, chainMode)
        visible = visible.slice(0, visibleChainCount)
      }
    }

    return visible
  }, [chains, chainMode])

  const searchTokens = useCallback(
    (searchTerm: string, tokens: PlugToken[]) =>
      tokens.filter(
        (t) =>
          t.symbol.toLowerCase().startsWith(searchTerm) ||
          t.name.toLowerCase().startsWith(searchTerm)
      ),
    []
  )

  const searchChains = useCallback(
    (searchTerm: string) =>
      chains.filter((chain) => {
        const { name, symbol } = getChainInfo(chain)

        return (
          name.toLowerCase().startsWith(searchTerm) || symbol.toLowerCase().startsWith(searchTerm)
        )
      }),
    [chains]
  )

  const filteredTokens = useMemo(() => {
    let tokenScope = tokensForSelect

    if (chainMode !== 'all') {
      tokenScope = tokenScope.filter((t) => t.chain === chainMode)
    }

    if (chainMode === Chain.BTC) {
      const odinBtc = tokensForSelect.find((t) => t.id === ODINBTC_ID)
      odinBtc && tokenScope.push(odinBtc)
    }

    return tokenScope
  }, [tokens, tokensForSelect, chainMode])

  return {
    type,
    setType,
    chainMode,
    getChainInfo,
    onSelect,
    onChainSelect,
    chains,
    visibleChains,
    visibleChainCount,
    showChains: visibleChains.length > 2,
    searchTokens,
    searchChains,
    filteredTokens,
  }
}
